/**
 * Shared "this element is a selectable item" contract for the file browser.
 *
 * Centralizes the selectable-item data attribute (which marks a file/row's DOM
 * node and carries its uid) so the file-browser mechanisms that key off it stay
 * in lockstep. Any item component opts in by spreading {@link selectableProps}.
 */

/** Data attribute marking a selectable file-browser item and carrying its uid. */
export const SELECTABLE_ATTR = "data-selectable-uid";

/** CSS selector matching every selectable item. */
export const SELECTABLE_SELECTOR = `[${SELECTABLE_ATTR}]`;

/** Spread onto an item's root element to mark it selectable: `{...selectableProps(uid)}`. */
export const selectableProps = (uid: string) => ({ [SELECTABLE_ATTR]: uid });

/** CSS selector matching the one selectable item carrying `uid`. */
const selectableSelector = (uid: string) =>
  `[${SELECTABLE_ATTR}="${CSS.escape(uid)}"]`;

/**
 * The selectable item's element for `uid`, to focus it or scroll it into view.
 * Searched within `root` (the whole document by default).
 */
export const findSelectableItem = (
  uid: string,
  root: ParentNode | null = document
): HTMLElement | null =>
  root?.querySelector<HTMLElement>(selectableSelector(uid)) ?? null;

/** Reads the uid off a selectable element (its `data-selectable-uid`). */
export const selectableUid = (el: HTMLElement): string | undefined =>
  el.dataset.selectableUid;
